import React, { useState, useEffect } from "react";
import { Activity, User as UserIcon, Send, Heart, Thermometer, Sparkles, Scale, Info, CheckCircle, ClipboardList } from "lucide-react";
import { Patient, Appointment, Vitals, Order } from "../types";
import { usePatient } from "../context/PatientContext";


interface NursingDashboardProps {
  user: { email: string; name: string; role: string };
  apiToken: string;
}

export default function NursingDashboard({ user, apiToken }: NursingDashboardProps) {
  const [patients, setPatients] = useState<Patient[]>([]);
  const { selectedPatient, setSelectedPatient } = usePatient();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Vitals Forms Input state
  const [bloodPressure, setBloodPressure] = useState("120/80");
  const [temp, setTemp] = useState("37.0");
  const [weight, setWeight] = useState("70");
  const [height, setHeight] = useState("170");
  const [targetBmi, setTargetBmi] = useState("");
  const [chiefComplaint, setChiefComplaint] = useState("");
  const [headCircumference, setHeadCircumference] = useState("");
  const [bmi, setBmi] = useState(24.2);

  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  useEffect(() => {
    fetchQueue();
  }, [apiToken]);

  useEffect(() => {
    if (selectedPatient) {
      setTargetBmi(selectedPatient.targetBmi ? String(selectedPatient.targetBmi) : "");
    }
  }, [selectedPatient]);
  
  // Recalculate BMI in real time
  useEffect(() => {
    const w = parseFloat(weight);
    const h = parseFloat(height) / 100; // convert cm to m
    if (w > 0 && h > 0) {
      const computedBmi = w / (h * h);
      setBmi(parseFloat(computedBmi.toFixed(1)));
    } else {
      setBmi(0);
    }
  }, [weight, height]);

  const fetchQueue = async () => {
    try {
      setError(null);
      // 1. Get Patients
      const pRes = await fetch("/api/patients", {
        headers: { Authorization: `Bearer ${apiToken}` },
      });
      const pData = await pRes.json();

      // 2. Get Appointments to see who is checked-in / pending
      const aRes = await fetch("/api/appointments", {
        headers: { Authorization: `Bearer ${apiToken}` },
      });
      const aData = await aRes.json();

      // 3. Get Orders (for NURSING tasks)
      const oRes = await fetch("/api/orders", {
        headers: { Authorization: `Bearer ${apiToken}` },
      });
      const oData = await oRes.json();

      if (pRes.ok && aRes.ok && oRes.ok) {
        setPatients(pData);
        setAppointments(aData);
        setOrders(oData.filter((o: Order) => o.department === "NURSING"));
      } else {
        setError("Error loading triage data. Please try again.");
      }
    } catch (err) {
      console.error("Failed to load nursing queue:", err);
      setError("Failed to connect to the server. Please check your network connection.");
    }
  };

  const submitVitals = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPatient) return;
    setLoading(true);

    try {
      if (targetBmi && !isNaN(parseFloat(targetBmi))) {
        await fetch(`/api/patients/${selectedPatient.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiToken}` },
          body: JSON.stringify({ targetBmi: parseFloat(targetBmi) })
        });
      }

      const res = await fetch("/api/vitals", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiToken}`,
        },
        body: JSON.stringify({
          patientId: selectedPatient.id,
          bloodPressure,
          temp,
          weight,
          height: parseFloat(height) || undefined,
          headCircumference: headCircumference ? parseFloat(headCircumference) : undefined,
          bmi,
          chiefComplaint,
          recordedBy: user.name,
        }),
      });

      if (res.ok) {
        setSuccessMsg(`Vitals successfully logged for ${selectedPatient.name}!`);
        setChiefComplaint("");
        // Refresh queue
        await fetchQueue();
        setTimeout(() => setSuccessMsg(""), 4000);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Filter patients currently checked in and waiting for consultation
  const waitingPatients = patients.filter((p) =>
    appointments.some((a) => a.patientId === p.id && a.status === "PENDING")
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-6">
      {/* Toast Alert */}
      {successMsg && (
        <div className="fixed top-6 right-6 z-50 bg-[#0F172A] text-white font-medium text-xs px-4 py-3 rounded-md shadow-md border border-[#E2E8F0] flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-[#10B981]" />
          <span>{successMsg}</span>
        </div>
      )}
      {error && (
        <div className="fixed top-6 right-6 z-50 bg-rose-50 text-rose-700 font-medium text-xs px-4 py-3 rounded-md shadow-md border border-rose-200 flex items-center gap-2">
          <Info className="w-4 h-4 text-rose-500" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-2 font-bold hover:text-rose-900">X</button>
        </div>
      )}

      {/* Header Panel */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-5 shadow-sm mb-6 flex justify-between items-center">
        <div>
          <span className="bg-[#F8FAFC] text-[#64748B] font-bold text-[9px] uppercase tracking-wider px-2 py-0.5 rounded border border-[#E2E8F0]">
            Nursing Service Node
          </span>
          <h1 className="text-base font-bold text-[#1E293B] mt-2">Rapid Vitals & Triage Center</h1>
          <p className="text-[#64748B] text-xs">Logged in as {user.name} • Alia & Hasan Command Interface</p>
        </div>
        <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-2 rounded-md flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#0F172A]" />
          <span className="text-[#64748B] text-[9px] font-mono tracking-wider font-semibold">Live Sync: ACTIVE</span>
        </div>
      </div>

      {/* Summary Statistics Widget */}


      {/* Split screen content layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left screen: Waiting queue */}
        {!selectedPatient && (
        <div className="lg:col-span-1 bg-white border border-[#E2E8F0] rounded-lg p-4 shadow-sm">
          <h3 className="font-bold text-[#1E293B] text-xs mb-4 flex items-center gap-2 uppercase tracking-wider text-[#64748B]">
            <Heart className="w-4 h-4 text-rose-500" />
            Waiting Queue ({waitingPatients.length})
          </h3>

          {waitingPatients.length > 0 ? (
            <div className="space-y-1.5 max-h-[500px] overflow-y-auto pr-1">
              {waitingPatients.map((p) => {
                const isSelected = selectedPatient?.id === p.id;
                const matchingAppt = appointments.find((a) => a.patientId === p.id && a.status === "PENDING");

                return (
                  <div
                    key={p.id}
                    onClick={() => setSelectedPatient(p)}
                    className={`p-2.5 rounded-md border cursor-pointer select-none transition flex items-center justify-between bg-white border-[#E2E8F0] hover:bg-[#F8FAFC]`}
                  >
                    <div>
                      <h4 className="font-bold text-[#1E293B] text-xs">{p.name}</h4>
                      <p className="text-[10px] font-mono text-[#64748B] mt-0.5">{p.phone}</p>
                    </div>
                    {matchingAppt && (
                      <span className="bg-[#F8FAFC] text-[#1E293B] border border-[#E2E8F0] font-semibold text-[8px] px-2 py-0.5 rounded uppercase tracking-wider">
                        {matchingAppt.department}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 border border-dashed border-[#E2E8F0] rounded-md">
              <UserIcon className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <h4 className="font-bold text-[#1E293B] text-xs uppercase tracking-wider text-[#64748B]">Queue is Empty</h4>
              <p className="text-[#64748B] text-[11px] mt-1 px-4 leading-relaxed font-medium">
                All arrived patients have been triaged. Waiting for new check-ins from front desk Laila/Fatema.
              </p>
            </div>
          )}
        </div>
        )}

        {/* Right screen: Rapid Vitals Entry Form */}
        <div className={`${!selectedPatient ? 'lg:col-span-2' : 'lg:col-span-3'} bg-white border border-[#E2E8F0] rounded-lg p-5 shadow-sm`}>
          {selectedPatient ? (
            <form onSubmit={submitVitals} className="space-y-6">
              <div className="flex items-center justify-between border-b border-[#E2E8F0] pb-3">
                <div>
                  <h3 className="font-bold text-[#1E293B] text-sm">Vitals Form Acquisition Panel</h3>
                  <p className="text-xs text-[#64748B] mt-0.5">Logging metrics for: <strong className="text-[#1E293B] font-bold">{selectedPatient.name}</strong></p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] text-[#0F172A] bg-[#F8FAFC] border border-[#E2E8F0] px-2 py-0.5 rounded-md font-mono font-medium">
                    DOB: {selectedPatient.dob}
                  </span>
                  <button 
                    type="button"
                    onClick={() => setSelectedPatient(null)}
                    className="bg-slate-100 hover:bg-slate-200 text-slate-600 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition"
                  >
                    Close Record
                  </button>
                </div>
              </div>

              {/* Grid vitals layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Blood Pressure Input */}
                <div>
                  <label className="block text-[11px] font-semibold text-[#64748B] uppercase tracking-wider mb-1.5 flex items-center justify-between">
                    Blood Pressure (mmHg)
                    {(() => {
                      /* Clinical safety threshold check with robust error handling for user entry parsing */
                      try {
                        const parts = bloodPressure.split('/');
                        if (parts.length === 2) {
                          const sys = parseInt(parts[0]);
                          const dia = parseInt(parts[1]);
                          if (!isNaN(sys) && !isNaN(dia)) {
                            if (sys > 140 || dia > 90) {
                              return <span className="text-rose-600 text-[9px] font-bold bg-rose-50 px-1.5 py-0.5 rounded border border-rose-200">⚠️ HYPERTENSION ALERT ({sys}/{dia})</span>;
                            }
                            if (sys < 90 || dia < 60) {
                              return <span className="text-amber-600 text-[9px] font-bold bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">⚠️ HYPOTENSION ALERT ({sys}/{dia})</span>;
                            }
                            return <span className="text-emerald-600 text-[9px] font-bold bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">NORMAL CLINICAL BP</span>;
                          }
                        }
                      } catch (err) {
                        // Suppress parse errors gracefully to prevent workspace runtime disruption
                      }
                      return null;
                    })()}
                  </label>
                  <input
                    type="text"
                    value={bloodPressure}
                    onChange={(e) => setBloodPressure(e.target.value)}
                    placeholder="e.g. 120/80"
                    className={`w-full bg-white border text-xs font-semibold px-3 py-2 rounded-md outline-none duration-150 ${(() => {
                      try {
                        const parts = bloodPressure.split('/');
                        if (parts.length === 2) {
                          const sys = parseInt(parts[0]);
                          const dia = parseInt(parts[1]);
                          if (!isNaN(sys) && !isNaN(dia)) {
                            if (sys > 140 || dia > 90) {
                              return 'border-rose-400 text-rose-700 bg-rose-50 focus:border-rose-600 focus:bg-rose-50/50';
                            }
                            if (sys < 90 || dia < 60) {
                              return 'border-amber-400 text-amber-700 bg-[#FFFDF5] focus:border-amber-500 focus:bg-white';
                            }
                            return 'border-emerald-200 text-[#1E293B] bg-emerald-50/10 focus:border-emerald-500 focus:bg-white';
                          }
                        }
                      } catch (err) {}
                      return 'border-[#E2E8F0] text-[#1E293B] focus:border-[#0F172A]';
                    })()}`}
                    required
                  />
                </div>

                {/* Patient Temp */}
                <div>
                  <label className="block text-[11px] font-semibold text-[#64748B] uppercase tracking-wider mb-1.5 flex items-center justify-between">
                    Body Temp (°C)
                    {(() => {
                      /* Clinical check on body temperature with strict validation */
                      try {
                        const t = parseFloat(temp);
                        if (!isNaN(t) && temp !== "") {
                          if (t > 37.5) {
                            return <span className="text-rose-600 text-[9px] font-bold bg-rose-50 px-1.5 py-0.5 rounded border border-rose-200">⚠️ FEVER DETECTED ({t}°C)</span>;
                          }
                          if (t < 35.5) {
                            return <span className="text-blue-600 text-[9px] font-bold bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200">⚠️ HYPOTHERMIA ALERT ({t}°C)</span>;
                          }
                          return <span className="text-emerald-600 text-[9px] font-bold bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">NORMAL TEMP</span>;
                        }
                      } catch (err) {}
                      return null;
                    })()}
                  </label>
                  <div className="relative flex items-center">
                    <input
                      type="number"
                      step="0.1"
                      value={temp}
                      onChange={(e) => setTemp(e.target.value)}
                      placeholder="e.g. 37.0"
                      className={`w-full bg-white border text-xs font-semibold px-3 py-2 rounded-md outline-none duration-150 ${(() => {
                        try {
                          const t = parseFloat(temp);
                          if (!isNaN(t) && temp !== "") {
                            if (t > 37.5) {
                              return 'border-rose-400 text-rose-700 bg-rose-50 focus:border-rose-600 focus:bg-rose-50/50';
                            }
                            if (t < 35.5) {
                              return 'border-blue-400 text-blue-700 bg-[#F5F8FF] focus:border-blue-600 focus:bg-white';
                            }
                            return 'border-emerald-200 text-[#1E293B] bg-emerald-50/10 focus:border-emerald-500 focus:bg-white';
                          }
                        } catch (err) {}
                        return 'border-[#E2E8F0] text-[#1E293B] focus:border-[#0F172A]';
                      })()}`}
                      required
                    />
                    <Thermometer className={`w-4 h-4 absolute right-3 ${(() => {
                      const t = parseFloat(temp);
                      if (!isNaN(t) && temp !== "") {
                        if (t > 37.5) return 'text-rose-500';
                        if (t < 35.5) return 'text-blue-500';
                        return 'text-emerald-500';
                      }
                      return 'text-[#64748B]';
                    })()}`} />
                  </div>
                </div>

                {/* Body Weight */}
                <div>
                  <label className="block text-[11px] font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Body Weight (kg)</label>
                  <input
                    type="number"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    placeholder="e.g. 75"
                    className="w-full bg-white border border-[#E2E8F0] text-xs text-[#1E293B] font-semibold px-3 py-2 rounded-md outline-none focus:border-[#0F172A] duration-150"
                    required
                  />
                </div>

                {/* Height cm */}
                <div>
                  <label className="block text-[11px] font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Height (cm)</label>
                  <input
                    type="number"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    placeholder="e.g. 175"
                    className="w-full bg-white border border-[#E2E8F0] text-xs text-[#1E293B] font-semibold px-3 py-2 rounded-md outline-none focus:border-[#0F172A] duration-150"
                    required
                  />
                </div>

                {/* Head Circumference (Only for < 5 years old) */}
                {selectedPatient?.dob && (new Date().getTime() - new Date(selectedPatient.dob).getTime()) / (1000 * 60 * 60 * 24 * 365.25) < 5 && (
                  <div>
                    <label className="block text-[11px] font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Head Circ. (cm)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={headCircumference}
                      onChange={(e) => setHeadCircumference(e.target.value)}
                      placeholder="e.g. 45.0"
                      className="w-full bg-white border border-[#E2E8F0] text-xs text-[#1E293B] font-semibold px-3 py-2 rounded-md outline-none focus:border-[#0F172A] duration-150"
                    />
                  </div>
                )}

                {/* Target BMI */}
                <div className="md:col-span-2">
                  <label className="block text-[11px] font-semibold text-[#10b981] uppercase tracking-wider mb-1.5 flex items-center gap-1"><div className="w-2 h-0.5 border-t-2 border-dashed border-[#10b981]"></div> Target BMI Goal (Optional)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={targetBmi}
                    onChange={(e) => setTargetBmi(e.target.value)}
                    placeholder="e.g. 22.5"
                    className="w-full bg-white border border-[#E2E8F0] text-xs text-[#1E293B] font-semibold px-3 py-2 rounded-md outline-none focus:border-[#10b981] duration-150"
                  />
                </div>

                {/* Auto Calculated BMI indicator */}
                {(() => {
                  /* Map clinical BMI categories to distinct functional dashboard styles with proper error handling */
                  let bgClass = "bg-[#F8FAFC] border-[#E2E8F0]";
                  let textTitleClass = "text-[#1E293B]";
                  let textSubClass = "text-[#64748B]";
                  let iconClass = "text-[#0F172A] bg-white border-[#E2E8F0]";
                  let bmiScoreClass = "text-[#0F172A]";
                  let bmiCategoryClass = "text-[#64748B]";
                  let label = "Validating Metrics...";

                  try {
                    if (bmi > 0) {
                      if (bmi < 18.5) {
                        bgClass = "bg-amber-50/70 border-amber-200";
                        textTitleClass = "text-amber-900";
                        textSubClass = "text-amber-700";
                        iconClass = "text-amber-700 bg-white border-amber-200";
                        bmiScoreClass = "text-amber-700 font-extrabold";
                        bmiCategoryClass = "text-amber-600";
                        label = "Underweight Warning";
                      } else if (bmi >= 18.5 && bmi < 25) {
                        bgClass = "bg-emerald-50/60 border-emerald-200";
                        textTitleClass = "text-emerald-950";
                        textSubClass = "text-emerald-700";
                        iconClass = "text-emerald-700 bg-white border-emerald-200";
                        bmiScoreClass = "text-emerald-700 font-extrabold";
                        bmiCategoryClass = "text-emerald-600";
                        label = "Healthy Clinical Weight";
                      } else if (bmi >= 25 && bmi < 30) {
                        bgClass = "bg-amber-50/40 border-amber-200/80";
                        textTitleClass = "text-amber-800";
                        textSubClass = "text-amber-600";
                        iconClass = "text-amber-600 bg-white border-amber-200";
                        bmiScoreClass = "text-amber-600 font-extrabold";
                        bmiCategoryClass = "text-amber-500";
                        label = "Overweight Warning";
                      } else {
                        bgClass = "bg-rose-50 border-rose-200";
                        textTitleClass = "text-rose-900";
                        textSubClass = "text-rose-700";
                        iconClass = "text-rose-600 bg-white border-rose-200 block";
                        bmiScoreClass = "text-rose-600 font-extrabold";
                        bmiCategoryClass = "text-rose-500";
                        label = "Obese Class (Alert)";
                      }
                    } else if (bmi === 0) {
                      label = "Enter height and weight";
                    }
                  } catch (err) {
                    console.error("Clinical error processing BMI category styling: ", err);
                  }

                  return (
                    <div className={`md:col-span-2 border p-3 rounded-md flex items-center justify-between transition-colors duration-200 ${bgClass}`}>
                      <div className="flex items-center gap-2.5">
                        <Scale className={`w-8 h-8 p-1.5 rounded-md border ${iconClass}`} />
                        <div>
                          <h4 className={`font-bold text-xs ${textTitleClass}`}>Body Mass Index (BMI Quotient)</h4>
                          <p className={`text-[10px] font-medium ${textSubClass}`}>Auto calculated on metric updates.</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`text-xl font-bold ${bmiScoreClass}`}>{bmi || "—"}</span>
                        <span className={`text-[9px] block font-semibold uppercase tracking-wider ${bmiCategoryClass}`}>
                          {label}
                        </span>
                      </div>
                    </div>
                  );
                })()}

                {/* Primary Chief Complaint */}
                <div className="md:col-span-2">
                  <label className="block text-[11px] font-semibold text-[#64748B] uppercase tracking-wider mb-1.5">Chief Complaint & Triage Notes</label>
                  <textarea
                    rows={4}
                    value={chiefComplaint}
                    onChange={(e) => setChiefComplaint(e.target.value)}
                    placeholder="Type primary reasons for clinical visit, systemic allergy flags, chronic aesthetic concerns..."
                    className="w-full bg-white border border-[#E2E8F0] rounded-md p-3 text-xs font-semibold text-[#1E293B] outline-none focus:border-[#0F172A] duration-150"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-10 bg-[#0F172A] hover:bg-slate-800 text-white font-bold rounded-md flex items-center justify-center gap-2 cursor-pointer transition select-none text-xs"
              >
                <Send className="w-3.5 h-3.5" />
                {loading ? "Registering vital ledger..." : "Dispatch Vitals Report to Physician Hub"}
              </button>
            </form>
          ) : (
            <div className="h-full flex flex-col justify-center items-center text-center p-12 py-24">
              <Heart className="w-10 h-10 text-slate-300 mb-3" />
              <h3 className="font-bold text-[#1E293B] text-xs uppercase tracking-wider text-[#64748B]">No Active Patient Selected</h3>
              <p className="text-[#64748B] text-xs max-w-xs leading-relaxed mt-1 font-medium">
                Select a checked-in patient from the left column queue list to acquire clinical vitals and complaint metadata.
              </p>
            </div>
          )}

          {/* Pending Nurse Orders Section */}
          {selectedPatient && (
            <div className="mt-6 border-t border-slate-200 pt-6">
              <h3 className="font-bold text-[#1E293B] text-xs uppercase tracking-wider flex items-center gap-2 mb-4">
                <ClipboardList className="w-4 h-4 text-emerald-500" /> Pending Physician Orders for Nurse
              </h3>
              {orders.filter(o => o.status === "PENDING" && o.patientId === selectedPatient?.id).length > 0 ? (
                <div className="space-y-3">
                  {orders.filter(o => o.status === "PENDING" && o.patientId === selectedPatient?.id).map(order => (
                    <div key={order.id} className="bg-emerald-50 border border-emerald-200 p-3 rounded-lg flex justify-between items-center gap-4">
                      <div>
                        <span className="text-[10px] bg-emerald-100 text-emerald-800 uppercase px-1.5 py-0.5 rounded font-bold border border-emerald-200">
                          NURSE TASK
                        </span>
                        <p className="text-xs text-slate-800 font-bold mt-1.5">{order.instruction}</p>
                        <p className="text-[10px] text-slate-500 font-mono mt-1">Order ID: {order.id}</p>
                      </div>
                      <button
                        onClick={async () => {
                          const confirmComplete = confirm("Mark this nursing task as completed?");
                          if (!confirmComplete) return;
                          try {
                            await fetch(`/api/orders/${order.id}/update`, {
                              method: "POST",
                              headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiToken}` },
                              body: JSON.stringify({ status: "COMPLETED", results: "Task completed by nursing staff" })
                            });
                            fetchQueue();
                            setSuccessMsg("Nursing task marked as complete!");
                            setTimeout(() => setSuccessMsg(""), 3000);
                          } catch(e) {}
                        }}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold px-3 py-1.5 rounded"
                      >
                        MARK DONE
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-500 italic">No pending nursing tasks for this patient.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
